/**
 * Release this tab's JetStream consumers when the page goes away.
 *
 * Every page load creates one durable consumer (plus an overlap shard while
 * two bounded filters overlap), named by a per-load nonce.
 * `FilterManager.teardown()` deletes them, but it only runs on React unmount;
 * a closed tab never completes that async delete, so its consumers would stay
 * on the stream until the broker's inactivity reap. Under launch concurrency
 * that population is what makes consumer create/update — a JetStream
 * control-plane call — slow enough to time out.
 *
 * On `pagehide` with `persisted === false` (a real close or navigation away,
 * not a bfcache park) this publishes one `$JS.API.CONSUMER.DELETE` request
 * per consumer, fire-and-forget: a publish is a synchronous write into the
 * socket buffer, and JetStream processes an API request without a reply
 * subject. bfcache parks keep their consumers so a restore resumes them.
 *
 * Platform-neutral: the unload seam is the adapter's optional
 * `appState.addPageHideListener` (web implements it; RN has no page).
 */

import type { AppStateSlot } from '../../platform/adapter.js';

export interface ConsumerReleaseOnUnloadDeps {
  readonly appState: Pick<AppStateSlot, 'addPageHideListener'>;
  readonly streamName: string;
  /** Durable consumer names currently owned by this tab. Read at unload time. */
  readonly getConsumerNames: () => readonly string[];
  /** `nc.publish` — fire-and-forget. */
  readonly publish: (subject: string) => void;
}

export function consumerDeleteSubject(streamName: string, consumerName: string): string {
  return `$JS.API.CONSUMER.DELETE.${streamName}.${consumerName}`;
}

/** Returns a disposer. No-op (and no listener) when the platform has no page-hide seam. */
export function installConsumerReleaseOnUnload(deps: ConsumerReleaseOnUnloadDeps): () => void {
  const addPageHideListener = deps.appState.addPageHideListener;
  if (addPageHideListener === undefined) return (): void => {};
  return addPageHideListener.call(deps.appState, (persisted: boolean): void => {
    if (persisted) return;
    for (const name of deps.getConsumerNames()) {
      try {
        deps.publish(consumerDeleteSubject(deps.streamName, name));
      } catch {
        // The socket may already be closing; the inactivity reap covers it.
      }
    }
  });
}
